import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import servicesApi from '../../../api/servicesApi';
import serviceOrdersApi from '../../../api/serviceOrdersApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import ServiceFormDialog from '../../../sections/admin/services/ServiceFormDialog';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });

export default function ServicesListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [serviceTypes, setServiceTypes] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadServiceTypes = async () => {
      try {
        const { data } = await serviceOrdersApi.getServiceTypes();
        setServiceTypes(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los tipos de servicio.'), {
          variant: 'error',
        });
      }
    };

    loadServiceTypes();
  }, [enqueueSnackbar]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await servicesApi.getServices({
        search: search || undefined,
        service_type_id: typeFilter || undefined,
        is_active: statusFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de servicios.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleNew = () => {
    setEditingService(null);
    setFormOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchServices();
  };

  const handleServiceTypeCreated = (type) => {
    setServiceTypes((prev) => [...prev, type].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await servicesApi.deleteService(serviceToDelete.id);
      enqueueSnackbar('Servicio eliminado correctamente.', { variant: 'success' });
      setServiceToDelete(null);
      fetchServices();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el servicio.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Nombre', flex: 1, minWidth: 220 },
      {
        field: 'service_type',
        headerName: 'Tipo de servicio',
        width: 170,
        valueGetter: (value, row) => row.service_type?.name || '—',
      },
      {
        field: 'price',
        headerName: 'Precio (S/)',
        width: 130,
        valueFormatter: (value) => currencyPen.format(value || 0),
      },
      {
        field: 'is_active',
        headerName: 'Estado',
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Activo' : 'Inactivo'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setServiceToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Servicios</Typography>
          <Typography variant="body2" color="text.secondary">
            Catálogo de servicios técnicos que el cliente puede solicitar desde la tienda.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo servicio
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nombre del servicio"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 4 }}>
            <TextField
              select
              fullWidth
              label="Tipo de servicio"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {serviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 4 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="1">Activo</MenuItem>
              <MenuItem value="0">Inactivo</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
        />
      </Card>

      <ServiceFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        service={editingService}
        serviceTypes={serviceTypes}
        onServiceTypeCreated={handleServiceTypeCreated}
      />

      <Dialog open={Boolean(serviceToDelete)} onClose={() => setServiceToDelete(null)}>
        <DialogTitle>Eliminar servicio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{serviceToDelete?.name}</strong>? Esta acción se puede revertir solo
            desde la base de datos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
